import { $generateHtmlFromNodes } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot } from 'lexical';
import type { FunctionComponent } from 'react';
import { useEffect, useRef } from 'react';

import { createRootNodesFromHtml, normalizeRichEditorHtml } from './rich-editor-html';

type ValueSyncPluginProps = {
    readonly value: string | undefined;
};

export const ValueSyncPlugin: FunctionComponent<ValueSyncPluginProps> = ({ value }) => {
    const [editor] = useLexicalComposerContext();
    const isFirstRender = useRef(true);
    const lastSyncedValue = useRef<string | undefined>(value);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;

            return;
        }

        if (value === lastSyncedValue.current) {
            return;
        }

        editor.update(
            () => {
                const currentHtml = $generateHtmlFromNodes(editor, null);

                if (normalizeRichEditorHtml(currentHtml) === value) {
                    lastSyncedValue.current = value;

                    return;
                }

                const root = $getRoot();

                root.clear().append(...createRootNodesFromHtml(editor, value));
                lastSyncedValue.current = value;
            },
            { tag: 'external-sync' },
        );
    }, [editor, value]);

    return null;
};
